/**
 * The SOC1.lab Terms & Conditions body — single source of truth, rendered both
 * by the passive footer modal (TermsFooter) and the blocking first-login gate
 * (TermsGate) so the two can never drift.
 */
import { Box, SpaceBetween } from "@cloudscape-design/components";

/** Bump when the T&C text materially changes (shown in the meta line). */
export const TERMS_VERSION = "v0.1";
export const TERMS_UPDATED = "2026-05-19";

export function TermsBody() {
  return (
    <>
      <Box variant="small" color="text-body-secondary">
        Owner: Sigon Group LLC ("Sigon") · Draft {TERMS_VERSION} · Last updated {TERMS_UPDATED}
      </Box>
      <SpaceBetween size="m">
        <Box variant="p">
          <strong>TL;DR.</strong> SOC1.lab is an internal review tool. The customer (user organization)
          uploads SOC 1 reports and warrants they are a Permitted User under the report's Access Agreement.
          Sigon acts as a processing tool, not as a recipient or redistributor. Sigon expresses no audit
          opinion. The customer indemnifies Sigon against any claim by a service auditor or service
          organization.
        </Box>

        <Box variant="h3">1. Permitted User Warranty</Box>
        <Box variant="p">
          Customer represents and warrants that, for each SOC 1, SOC 2, or similar third-party attestation
          report ("Service Auditor Report") Customer uploads to or processes through SOC1.lab, Customer is a
          "Permitted User," "user entity," or other authorized recipient under the access terms governing
          that report (an "Access Agreement"), and Customer holds all rights and authority necessary to
          submit the report to SOC1.lab for the purposes described herein.
        </Box>

        <Box variant="h3">2. Service Role — Processing Agent</Box>
        <Box variant="p">
          Customer engages Sigon Group LLC ("Sigon") solely as Customer's internal processing tool to assist
          Customer's review and assessment of Service Auditor Reports for Customer's own internal control
          and compliance purposes (including SOX 404 reliance). Sigon does not act as a recipient,
          redistributor, publisher, or further-disclosing party of any Service Auditor Report. Customer's
          use of SOC1.lab constitutes internal use by Customer within the scope of the applicable Access
          Agreement.
        </Box>

        <Box variant="h3">3. No Reliance, No Opinion</Box>
        <Box variant="p">
          Sigon does not perform attestation, audit, or assurance services. Sigon expresses no opinion on,
          and does not adopt, ratify, or assume responsibility for, any Service Auditor Report, the service
          organization's system description, the control objectives, the controls, or the service auditor's
          conclusions. Outputs generated by SOC1.lab are working drafts to assist Customer's professional
          judgment; Customer's auditors and Customer remain solely responsible for all conclusions, reliance
          decisions, and workpapers.
        </Box>

        <Box variant="h3">4. Confidentiality Flow-Down</Box>
        <Box variant="p">
          Sigon shall treat each Service Auditor Report and its contents as Customer Confidential
          Information, restrict access to personnel and subprocessors with a need to know, and not disclose,
          distribute, quote, reference, publish, or incorporate any Service Auditor Report (or material
          portions thereof) outside SOC1.lab except as required to deliver SOC1.lab to Customer or as
          required by law. Sigon will not use Service Auditor Reports to train shared or third-party
          machine-learning models, and will not use them to train its own models beyond per-tenant retrieval
          and per-Customer processing.
        </Box>

        <Box variant="h3">5. Prohibited Uses</Box>
        <Box variant="p">
          Customer shall not use SOC1.lab, or any outputs generated by SOC1.lab, to (a) further disclose,
          distribute, quote, or reference a Service Auditor Report to any party not authorized under the
          applicable Access Agreement; (b) incorporate a Service Auditor Report by reference in any
          securities filing or other public document; or (c) take any action that would breach the
          applicable Access Agreement. Customer is solely responsible for compliance with each Access
          Agreement and for confirming the scope of its Permitted User rights before upload.
        </Box>

        <Box variant="h3">6. Indemnification</Box>
        <Box variant="p">
          Customer shall defend, indemnify, and hold harmless Sigon, its affiliates, and their respective
          personnel from and against any claim, loss, liability, or expense (including reasonable attorneys'
          fees) arising out of or relating to (a) Customer's breach of Sections 1, 2, or 5; (b) any claim by
          a service organization, service auditor (including their subsidiaries, subcontractors, and
          personnel), or other third party that Customer's submission, processing, or use of a Service
          Auditor Report through SOC1.lab exceeded Customer's rights under the applicable Access Agreement;
          or (c) any indemnity, release, or hold-harmless obligation Customer owes to a service auditor or
          service organization under any Access Agreement.
        </Box>

        <Box variant="h3">7. Data Retention and Deletion</Box>
        <Box variant="p">
          Sigon will retain Service Auditor Reports and Customer-generated workpapers only as long as
          necessary to deliver SOC1.lab and will delete them within ninety (90) days of engagement close or
          upon Customer request, subject to applicable legal-hold requirements.
        </Box>

        <Box variant="h3">8. Customer Acknowledgement</Box>
        <Box variant="p">
          Customer acknowledges that Access Agreements typically (a) limit access to specified user entities
          and their external auditors, (b) prohibit further disclosure and incorporation by reference, and
          (c) disclaim service auditor liability to non-Permitted Users. Customer affirms that nothing in
          these Terms modifies, waives, or reduces those terms as between Customer and the service auditor
          or service organization.
        </Box>

        <Box variant="h3">9. Acceptance by Use</Box>
        <Box variant="p">
          By accessing or using SOC1.lab, Customer acknowledges that Customer has read, understood, and
          agreed to these Terms on behalf of Customer's organization, and that the individual accepting is
          authorized to bind that organization.
        </Box>
      </SpaceBetween>
    </>
  );
}
